import { prefetchMyVariables } from '@/hooks/queries/variables/variables-v2';
import UserService from '@/services/user-service';
import { useAuthStore } from '@/store/use-auth-store';
import { useUserStore } from '@/store/use-user-store';
import { QueryClient } from '@tanstack/react-query';
import { AuthMeResponse, AuthMeUser } from './auth-me';
import { applyAuthMeScope } from './sync-auth-scope';

export const AUTH0_ESTABLISHED_SUB_KEY = 'lift-auth0-established-sub';

/** Apply /auth/me/ payload to client stores — single source of truth for profile. */
export function applyAuthMeToStores(data: AuthMeResponse): AuthMeUser {
  const { user, tenants } = data;

  if (!user) {
    throw new Error('User not found');
  }

  useAuthStore.getState().setTenants(tenants || []);
  useUserStore.getState().setUser({
    id: user.id,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
  });
  applyAuthMeScope(data);

  return user;
}

export async function syncSessionFromBackend(options?: {
  skipUnauthorizedRedirect?: boolean;
  tenantId?: number;
  queryClient?: QueryClient;
}): Promise<AuthMeResponse> {
  const data = (await UserService.getCurrentUser({
    skipUnauthorizedRedirect: options?.skipUnauthorizedRedirect,
    tenantId: options?.tenantId,
  })) as AuthMeResponse;

  applyAuthMeToStores(data);

  if (options?.queryClient) {
    await prefetchMyVariables(options.queryClient, undefined, {
      tenantId: options.tenantId,
    });
    void options.queryClient.invalidateQueries({
      queryKey: ['userPermissions'],
    });
  }

  return data;
}

export function getEstablishedAuth0Sub(): string | null {
  return sessionStorage.getItem(AUTH0_ESTABLISHED_SUB_KEY);
}

export function markEstablishedAuth0Sub(sub: string): void {
  sessionStorage.setItem(AUTH0_ESTABLISHED_SUB_KEY, sub);
}

export function clearEstablishedAuth0Sub(): void {
  sessionStorage.removeItem(AUTH0_ESTABLISHED_SUB_KEY);
}

/** True when Auth0 identity changed since the last successful SSO session setup. */
export function hasAuth0UserChanged(sub: string | undefined | null): boolean {
  if (!sub) {
    return false;
  }

  const established = getEstablishedAuth0Sub();
  return established !== null && established !== sub;
}

export function getStoredBackendUserId(): number | null {
  const id = useUserStore.getState().user?.id;
  if (id == null) {
    return null;
  }

  const parsed = Number(id);
  return Number.isNaN(parsed) ? null : parsed;
}

export function isBackendUserMismatch(data: AuthMeResponse): boolean {
  const storedId = getStoredBackendUserId();
  if (storedId == null || !data.user?.id) {
    return false;
  }

  return storedId !== data.user.id;
}

/** Query keys tied to the signed-in user — safe to drop on login / account switch. */
const AUTH_SCOPED_QUERY_KEYS = [
  'userPermissions',
  'userMyPermissions',
  'user-variables',
  'variables-entries',
  'tenants',
  'tenant-config',
  'user-orgs',
  'identity-organizations',
  'users',
  'user',
  'accessKeys',
  'accessKey',
] as const;

export function clearAuthQueryCache(queryClient: QueryClient): void {
  for (const key of AUTH_SCOPED_QUERY_KEYS) {
    queryClient.removeQueries({ queryKey: [key] });
  }
}
